import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatCurrency, parseAmount } from "@/lib/money";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { CreditNoteCreatePayload, Invoice } from "@/types/camping";

export interface CreateCreditNoteModalProps {
  open: boolean;
  sourceInvoice: Invoice | null;
  onClose: () => void;
  onCreated?: (invoiceId: string) => void;
}

export function CreateCreditNoteModal({
  open,
  sourceInvoice,
  onClose,
  onCreated,
}: CreateCreditNoteModalProps) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sourceInvoice) return;
    const paid = Number(sourceInvoice.paid_amount ?? 0);
    const suggested = Number.isFinite(paid) && paid > 0 ? paid : Number(sourceInvoice.total_amount);
    setDescription(`Gutschrift zu Rechnung ${sourceInvoice.invoice_number}`);
    setAmount(Number.isFinite(suggested) && suggested > 0 ? String(suggested) : "");
    setNotes("Erstattung wegen vorzeitiger Abreise");
    setError(null);
  }, [open, sourceInvoice]);

  async function submit() {
    if (!sourceInvoice) return;
    const parsed = parseAmount(amount);
    if (!description.trim()) {
      setError("Bitte eine Beschreibung eingeben.");
      return;
    }
    if (parsed == null || parsed <= 0) {
      setError("Bitte einen gültigen Betrag größer 0 eingeben.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: CreditNoteCreatePayload = {
        guest_id: sourceInvoice.guest_id,
        booking_id: sourceInvoice.booking_id,
        reference_invoice_id: sourceInvoice.id,
        lines: [
          {
            description: description.trim(),
            amount: parsed.toFixed(2),
          },
        ],
        notes: notes.trim() || null,
      };
      const created = await api.post<Invoice>("/api/v1/invoices/credit-note", payload);
      onCreated?.(created.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gutschrift konnte nicht erstellt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!sourceInvoice) return null;

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      title="Gutschrift erstellen"
      description={`Bezug: Rechnung ${sourceInvoice.invoice_number}`}
      size="md"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-sm text-label-secondary">
          Erstellt einen Gutschrift-Entwurf. Nach dem Ausstellen geht die Erstattung bevorzugt über
          das ursprüngliche Zahlungsmittel zurück: Stripe, EC-Terminal oder bar. Die Art kann
          dabei geändert werden.
        </p>

        {sourceInvoice.paid_amount ? (
          <p className="text-xs text-label-secondary tabular-nums">
            Bereits bezahlt auf Referenzrechnung:{" "}
            {formatCurrency(sourceInvoice.paid_amount, sourceInvoice.currency)}
          </p>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          Beschreibung
          <input
            type="text"
            required
            className="eh-input"
            maxLength={500}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          Erstattungsbetrag (brutto, {sourceInvoice.currency})
          <input
            type="number"
            min="0.01"
            step="0.01"
            required
            className="eh-input tabular-nums"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          Notiz <span className="text-label-tertiary">(optional)</span>
          <textarea
            className="eh-input min-h-[2.5rem] resize-y"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" disabled={submitting} onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting} disabled={submitting}>
            Gutschrift-Entwurf anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
